import {
	Component,
	ComponentRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
	ViewChild,
	ViewContainerRef
} from '@angular/core';
import type { PdfViewerComponent } from 'ng2-pdf-viewer';
import { IPreviewableDocument } from '../../../models/documents/document.model';

@Component({
	selector: 'app-document-preview',
	templateUrl: './document-preview.component.html',
	styleUrls: ['./document-preview.component.scss']
})
export class DocumentPreviewComponent implements OnChanges, OnDestroy {
	@Input() document: IPreviewableDocument | null = null;
	@Input() previewUrl: string | null = null;
	@Input() isLoading = false;
	@Input() showDelete = false;
	@Input() isDeleting = false;

	@Output() download = new EventEmitter<void>();
	@Output() remove = new EventEmitter<void>();

	zoomLevel = 1.0;

	private pdfHostRef?: ViewContainerRef;
	private pdfViewerRef?: ComponentRef<PdfViewerComponent>;
	pdfLoadFailed = false;

	/**
	 * DOVE-2520 — ng2-pdf-viewer (with pdfjs-dist, ~485 kB) used to come in through SharedModule,
	 * which AppModule imports, so it sat in the initial bundle of every page. It is now imported
	 * the first time a PDF is actually shown and created into this placeholder.
	 */
	@ViewChild('pdfHost', { read: ViewContainerRef })
	set pdfHost(host: ViewContainerRef | undefined) {
		if (host === this.pdfHostRef) return;
		this.pdfHostRef = host;
		this.pdfViewerRef = undefined; // the old host's view, if any, is destroyed with it
		if (host) {
			void this.mountPdfViewer(host);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['document'] && changes['document'].currentValue !== changes['document'].previousValue) {
			this.zoomLevel = 1.0;
		}
		this.syncPdfViewer();
	}

	onZoomIn(): void {
		if (this.zoomLevel < 2.0) {
			this.zoomLevel += 0.25;
			this.syncPdfViewer();
		}
	}

	onZoomOut(): void {
		if (this.zoomLevel > 0.5) {
			this.zoomLevel -= 0.25;
			this.syncPdfViewer();
		}
	}

	getZoomPercentage(): number {
		return Math.round(this.zoomLevel * 100);
	}

	onDownload(): void {
		this.download.emit();
	}

	onRemove(): void {
		this.remove.emit();
	}

	ngOnDestroy(): void {
		// The viewer lives in this component's view and is destroyed with it; the preview URL
		// itself is cleaned up by the parent component.
		this.pdfHostRef = undefined;
		this.pdfViewerRef = undefined;
	}

	private async mountPdfViewer(host: ViewContainerRef): Promise<void> {
		this.pdfLoadFailed = false;
		let PdfViewerComponent: typeof import('ng2-pdf-viewer').PdfViewerComponent;
		try {
			({ PdfViewerComponent } = await import('ng2-pdf-viewer'));
		} catch (error) {
			// Typically a tab left open across a deploy: its viewer chunk name no longer exists.
			// Ask for a manual reload rather than reloading, which could lose unsaved work
			// on screens like Confirming.
			console.error('Failed to load the PDF viewer', error);
			this.pdfLoadFailed = true;
			return;
		}
		// The placeholder may have gone (document changed, dialog closed) while the chunk loaded.
		if (host !== this.pdfHostRef) return;

		host.clear();
		const viewer = host.createComponent(PdfViewerComponent);
		viewer.location.nativeElement.classList.add('pdf-viewer');
		viewer.setInput('render-text', true);
		viewer.setInput('original-size', false);
		viewer.setInput('show-all', true);
		viewer.setInput('fit-to-page', false);
		viewer.setInput('autoresize', true);
		this.pdfViewerRef = viewer;
		this.syncPdfViewer();
	}

	/** Push the inputs that change after creation: the file and the zoom level. */
	private syncPdfViewer(): void {
		if (!this.pdfViewerRef || !this.previewUrl) return;
		this.pdfViewerRef.setInput('src', this.previewUrl);
		this.pdfViewerRef.setInput('zoom', this.zoomLevel);
	}
}
